import {
    Badge,
    Box,
    Button,
    Card,
    Container,
    Group,
    Loader,
    LoadingOverlay,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
} from '@mantine/core';
import { CAMPAIGN_TYPES, OFFER_CATEGORIES } from '../../../common/enums/enums';
import {
    CustomMultiSelect,
    MultiSelectCreatable,
    SearchableSelect,
    SearchableSelectCreatable,
} from '../../../common';
import React, { useEffect, useMemo, useState } from 'react';
import ScheduleSubview, {
    DAY_DEFS,
    DEFAULT_SCHEDULE,
} from './components/ScheduleSubview';
import {
    getShortlinks,
    getUserVerticals,
    getUsers,
    getVerticals,
} from '../../../../http/apiClient';
import { useNavigate, useParams } from 'react-router-dom';

import EmailCampaignSubview from './components/EmailCampaignSubview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RvmCampaignSubview from './components/RvmCampaignSubview';
import SmsCampaignSubview from './components/SmsCampaignSubview';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import { toGroupedOptions } from '../../../../utils/enumUtils';
import useAddCampaign from '../../../../http/mutations/campaigns/useAddCampaign';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';
import useGetCampaignByIdQuery from '../../../../http/queries/campaigns/useGetCampaignById';
import { useTimeout } from '@mantine/hooks';

const CreateCampaignView = ({}) => {
    const { id: campaignId } = useParams();
    const userProfile = useAuthStore((state) => state.userProfile);

    const { isLoading, data: campaign } = useGetCampaignByIdQuery(campaignId);

    const setInitialValues = () => ({
        id: Number.isInteger(Number.parseInt(campaignId)) ? campaignId : null,
        name: campaign?.name || '',
        userId: hasRole(userProfile, 'SUPER_ADMIN')
            ? (campaign?.userId ?? userProfile?.id.toString())
            : null,
        type: campaign?.type || '',

        routeId: campaign?.route?.id.toString(),
        dataSetId: campaign?.dataset?.value,
        clickerDataSetId: campaign?.clickerDataSet?.value,
        dataManager: campaign?.dataManager || 'Default',

        smsOptions: {
            messageGroupId:
                campaign?.type === 'Sms' ? campaign.messageGroup?.value : null,
            linkGroupId:
                campaign?.type === 'Sms' ? campaign.linkGroup?.value : null,
            /*mediaGroupId:
                campaign?.type === 'Sms' ? campaign.mediaGroup?.value : null,*/
        },
        rvmOptions: {
            messageGroupId:
                campaign?.type === 'Rvm' ? campaign.messageGroup?.value : null,
            linkGroupId:
                campaign?.type === 'Rvm' ? campaign.linkGroup?.value : null,
        },
        emailOptions: {
            messageGroupId:
                campaign?.type === 'Email'
                    ? campaign.messageGroup?.value
                    : null,
        },
        shortLinkId: campaign?.shortLink?.value,

        scheduleEnabled: campaign?.scheduleEnabled || false,
        scheduleOptions:
            campaign?.scheduleOptions ||
            DAY_DEFS.reduce((acc, { key }) => {
                acc[key] = {
                    ...DEFAULT_SCHEDULE,
                };
                return acc;
            }, {}),

        category: campaign?.category || '',
        vertical: campaign?.vertical || '',
        trackingId: campaign?.trackingId || '',
        tags: campaign?.tags || [],
    });

    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? userProfile?.id.toString()
                : null,
            smsOptions: {},
            rvmOptions: {},
            emailOptions: {},
            scheduleOptions: {},
        },
        transformValues: (values) => ({
            ...values,
            trackingId: values.trackingId || null,
        }),
        onSubmitPreventDefault: 'always',

        validate: {
            'name': (value) => {
                const { type, message } = genericValidator(
                    'Campaign name',
                    value,
                    {
                        required: true,
                        minLength: 6,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Campaign name must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            'smsOptions.messageGroupId': (value) => {
                if (form.values.type !== 'Sms') {
                    return;
                }
                const { message } = genericValidator('Message group', value, {
                    required: true,
                });
                return message;
            },

            'smsOptions.linkGroupId': (value) => {
                if (form.values.type !== 'Sms') {
                    return;
                }
                const { message } = genericValidator('Link group', value, {
                    required: true,
                });
                return message;
            },

            'rvmOptions.messageGroupId': (value) => {
                if (form.values.type !== 'Rvm') {
                    return;
                }
                const { message } = genericValidator('Message group', value, {
                    required: true,
                });
                return message;
            },
            'rvmOptions.linkGroupId': (value) => {
                if (form.values.type !== 'Rvm') {
                    return;
                }
                const { message } = genericValidator('Link group', value, {
                    required: true,
                });
                return message;
            },

            'emailOptions.messageGroupId': (value) => {
                if (form.values.type !== 'Email') {
                    return;
                }
                const { message } = genericValidator('Message group', value, {
                    required: true,
                });
                return message;
            },
            'emailOptions.linkGroupId': (value) => {
                if (form.values.type !== 'Email') {
                    return;
                }
                const { message } = genericValidator('Link group', value, {
                    required: true,
                });
                return message;
            },
            'userId': (value) => {
                const { message } = genericValidator('User ID', value, {
                    required: hasRole(userProfile, 'SUPER_ADMIN'),
                });
                return message;
            },
            'routeId': (value) => {
                const { message } = genericValidator('Route', value, {
                    required: true,
                });
                return message;
            },
            'dataSetId': (value) => {
                const { message } = genericValidator('Data set', value, {
                    required: true,
                });
                return message;
            },
            'dataManager': (value) => {
                const { message } = genericValidator('Data Manager', value, {
                    required: true,
                });
                return message;
            },
            'chatHandlerId': (value) => {
                const { message } = genericValidator('Chat Handler', value, {
                    required: false,
                });
                return message;
            },
            'type': (value) => {
                const { message } = genericValidator('Type', value, {
                    required: true,
                });
                return message;
            },
            'category': (value) => {
                const { message } = genericValidator('Category', value, {
                    required: true,
                });
                return message;
            },
            'vertical': (value) => {
                const { type, message } = genericValidator('Vertical', value, {
                    required: false,
                    minLength: 4,
                    maxLength: 60,
                    pattern: /^[a-zA-Z0-9_-]+$/,
                });
                if (type) {
                    return type === 'pattern'
                        ? 'Vertical must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            'trackingId': (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            'tags': (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    useEffect(() => {
        if (campaign && form.values.id != campaign.id) {
            form.setInitialValues(setInitialValues());
            form.reset();
        }
    }, [campaign?.id]);

    const CampaignTypeSubView = useMemo(() => {
        switch (form.values.type) {
            case 'Sms':
                return SmsCampaignSubview;

            case 'Rvm':
                return RvmCampaignSubview;

            case 'Email':
                return EmailCampaignSubview;

            default:
                return;
        }
    }, [form.getValues('type')]);

    const { isPending, isSuccess, mutate: addOrUpdate } = useAddCampaign();

    const navigate = useNavigate();
    const { start } = useTimeout(() => navigate('/campaigns'), 2000);

    const onSubmit = (model) => {
        if (model.type !== 'Sms') model.smsOptions = null;
        if (model.type !== 'Rvm') model.rvmOptions = null;
        if (model.type !== 'Email') model.emailOptions = null;

        if (!model.scheduleEnabled) {
            model.scheduleOptions = null;
        }

        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                start();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    if (isLoading) {
        return (
            <Container fluid>
                <Loader visible={isLoading} />
            </Container>
        );
    }

    return (
        <Container fluid>
            <Card>
                <Text
                    fw={500}
                    size='lg'
                    mt='md'>
                    {campaign ? 'Update Campaign' : 'Create Campaign'}
                </Text>
                <Card.Section p={'lg'}>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <TextInput
                                label={'Campaign Name'}
                                placeholder='Enter a name'
                                withAsterisk
                                key={form.key('name')}
                                {...form.getInputProps('name')}
                            />

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <SearchableSelect
                                    label={'User'}
                                    withAsterisk
                                    placeholder='User'
                                    queryHttpFn={getUsers}
                                    queryKey={'users'}
                                    optionsMapFn={(user) => ({
                                        label: user.username,
                                        value: user.id.toString(),
                                    })}
                                    defaultValue={
                                        campaign?.owner || {
                                            label: userProfile.username,
                                            value: userProfile.id,
                                        }
                                    }
                                    key={form.key('userId')}
                                    {...form.getInputProps('userId')}
                                />
                            ) : null}

                            <Select
                                label={'Type'}
                                placeholder='Select campaign type'
                                data={CAMPAIGN_TYPES.filter(
                                    (x) => x.value !== 'None'
                                )}
                                withAsterisk
                                key={form.key('type')}
                                {...form.getInputProps('type')}
                            />

                            {form.values.type ? (
                                <SearchableSelect
                                    label={'Short Link'}
                                    placeholder='Select short link.  Leave empty for auto-assigned shortlink.'
                                    queryHttpFn={(queryParams) =>
                                        getShortlinks(queryParams)
                                    }
                                    queryKey={'short_links'}
                                    optionsMapFn={(x) => ({
                                        label: x.domain,
                                        value: x.id.toString(),
                                    })}
                                    defaultValue={campaign?.shortLink}
                                    key={form.key('shortLinkId')}
                                    {...form.getInputProps('shortLinkId')}
                                />
                            ) : null}

                            {CampaignTypeSubView ? (
                                <CampaignTypeSubView
                                    form={form}
                                    campaign={campaign}
                                />
                            ) : null}

                            {form.values.type ? (
                                <>
                                    <Select
                                        searchable
                                        label={'Category'}
                                        placeholder='Select a category'
                                        data={toGroupedOptions(
                                            OFFER_CATEGORIES
                                        )}
                                        withAsterisk
                                        key={form.key('category')}
                                        {...form.getInputProps('category')}
                                    />

                                    <SearchableSelectCreatable
                                        label={'Vertical'}
                                        placeholder='Select or create a new vertical'
                                        queryHttpFn={(queryParams) => {
                                            const userId =
                                                form.values['userId'];
                                            const searchUserVerticals =
                                                hasRole(
                                                    userProfile,
                                                    'SUPER_ADMIN'
                                                ) && userId;

                                            if (searchUserVerticals) {
                                                return getUserVerticals({
                                                    userId,
                                                    pagination: queryParams,
                                                });
                                            }
                                            return getVerticals(queryParams);
                                        }}
                                        queryKey={'verticals'}
                                        optionsMapFn={(vertical) => ({
                                            label: vertical,
                                            value: vertical,
                                        })}
                                        key={form.key('vertical')}
                                        {...form.getInputProps('vertical')}
                                    />

                                    <TextInput
                                        label={'Tracking Id'}
                                        placeholder={'Custom tracking id'}
                                        key={form.key('trackingId')}
                                        {...form.getInputProps('trackingId')}
                                    />

                                    <TagsInput
                                        label={'Tags'}
                                        placeholder={'Tags'}
                                        key={form.key('tags')}
                                        {...form.getInputProps('tags')}
                                    />
                                </>
                            ) : null}

                            <ScheduleSubview
                                form={form}
                                campaign={campaign}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={
                                        isPending ||
                                        isSuccess ||
                                        !form.isValid()
                                    }
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    Create
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Card.Section>
            </Card>
        </Container>
    );
};
export default CreateCampaignView;
